// Shared chat/workspace upload config + validation, used by both the in-project
// chat composer (GeminiChatArea) and the new-project seed composer
// (ProjectSeedComposer) so the two attach surfaces stay in lock-step.
import { getWorkspaceUploadConfig } from '../../apiService';

export interface ChatUploadConfig {
    maxBytes: number;
    // BAR-308: files larger than this use the signed-URL direct-to-GCS flow.
    // Authoritative value comes from `/uploads/config`; the default below is a
    // pre-fetch fallback mirroring the backend (8 MiB).
    multipartMaxBytes: number;
    maxFilesPerWorkspace: number;
    allowedMimeTypes: Set<string>;
    allowedExtensions: Set<string>;
}

export const DEFAULT_CHAT_UPLOAD_CONFIG: ChatUploadConfig = {
    // BAR-308: pre-fetch fallback bumped to the 1 GiB product cap. The
    // authoritative value still comes from `/uploads/config`; signed-URL
    // direct-to-GCS removes the ~32 MiB Cloud Run buffered-request ceiling.
    maxBytes: 1024 * 1024 * 1024,
    multipartMaxBytes: 8 * 1024 * 1024,
    maxFilesPerWorkspace: 100,
    allowedMimeTypes: new Set([
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/csv',
        'image/png',
        'image/jpeg',
        'image/webp',
    ]),
    allowedExtensions: new Set([
        'pdf',
        'txt',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'ppt',
        'pptx',
        'csv',
        'png',
        'jpg',
        'jpeg',
        'webp',
    ]),
};

// The file-input `accept` attribute, DERIVED from allowedExtensions so a new
// extension can never be silently hidden from the picker.
export const CHAT_UPLOAD_ACCEPT = Array.from(DEFAULT_CHAT_UPLOAD_CONFIG.allowedExtensions)
    .map((ext) => `.${ext}`)
    .join(',');

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isSupportedUploadFile(file: File, config: ChatUploadConfig): boolean {
    if (file.type && config.allowedMimeTypes.has(file.type.toLowerCase())) return true;
    const extension = file.name.split('.').pop()?.toLowerCase();
    return !!extension && config.allowedExtensions.has(extension);
}

// Fetch the authoritative upload config, falling back to the pre-fetch default
// on any failure. Never throws.
export async function loadChatUploadConfig(): Promise<ChatUploadConfig> {
    try {
        const result = await getWorkspaceUploadConfig();
        if (!result.success || !result.config) return DEFAULT_CHAT_UPLOAD_CONFIG;
        return {
            maxBytes: result.config.max_bytes || DEFAULT_CHAT_UPLOAD_CONFIG.maxBytes,
            multipartMaxBytes:
                result.config.multipart_max_bytes ?? DEFAULT_CHAT_UPLOAD_CONFIG.multipartMaxBytes,
            maxFilesPerWorkspace:
                result.config.max_files_per_workspace ?? DEFAULT_CHAT_UPLOAD_CONFIG.maxFilesPerWorkspace,
            allowedMimeTypes: new Set(
                result.config.allowed_mime_types ||
                    Array.from(DEFAULT_CHAT_UPLOAD_CONFIG.allowedMimeTypes),
            ),
            allowedExtensions: new Set(
                result.config.allowed_extensions ||
                    Array.from(DEFAULT_CHAT_UPLOAD_CONFIG.allowedExtensions),
            ),
        };
    } catch {
        return DEFAULT_CHAT_UPLOAD_CONFIG;
    }
}
